import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, RotateCcw, Loader2, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { API, QUIZ_QUESTIONS } from "../lib/constants";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

export default function Quiz() {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [contact, setContact] = useState({ name: "", email: "", company: "" });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const total = QUIZ_QUESTIONS.length;
  const isContactStep = step === total;
  const isResultStep = !!result;
  const progress = Math.min(((step + (isContactStep ? 1 : 0)) / (total + 1)) * 100, 100);

  const current = QUIZ_QUESTIONS[step];

  const choose = (val) => {
    setAnswers((a) => ({ ...a, [current.id]: val }));
    setTimeout(() => setStep((s) => s + 1), 180);
  };

  const submit = async () => {
    setLoading(true);
    try {
      const payload = { ...answers, ...contact };
      const res = await axios.post(`${API}/quiz`, payload);
      setResult(res.data?.result);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not calculate. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setStep(0);
    setAnswers({});
    setContact({ name: "", email: "", company: "" });
    setResult(null);
  };

  return (
    <main className="pt-32 md:pt-40 pb-24 min-h-screen bg-slate-50" data-testid="quiz-page">
      <div className="max-w-3xl mx-auto px-6 md:px-10">
        <Link to="/" data-testid="quiz-back-home" className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1.5 mb-8">
          <ArrowLeft className="h-4 w-4" /> Back to Home
        </Link>

        <div className="mb-8">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-teal-700 mb-2">
            Portfolio Health Quiz
          </div>
          <h1 className="font-display text-3xl md:text-5xl font-extrabold text-slate-900 leading-[1.06]">
            How much value is hiding <br />
            <span className="font-editorial italic font-medium text-slate-700">in your mature & tail-end portfolio?</span>
          </h1>
          <p className="text-slate-600 mt-4 text-base md:text-lg max-w-2xl">
            5 quick questions. ~60 seconds. Get an instant estimate of revenue opportunity and OPEX
            savings — calibrated to your portfolio.
          </p>
        </div>

        <div className="bg-white border border-slate-200 p-6 md:p-10 min-h-[420px] flex flex-col">
          {/* Progress */}
          <div className="mb-8">
            <div className="flex justify-between text-[11px] font-bold uppercase tracking-[0.16em] text-slate-500 mb-2">
              <span data-testid="quiz-step-indicator">
                {isResultStep ? "Result" : isContactStep ? "Almost there" : `Step ${step + 1} of ${total}`}
              </span>
              <span>{Math.round(isResultStep ? 100 : progress)}%</span>
            </div>
            <div className="h-1 bg-slate-100">
              <motion.div
                className="h-full bg-teal-600"
                animate={{ width: `${isResultStep ? 100 : progress}%` }}
                transition={{ duration: 0.4 }}
              />
            </div>
          </div>

          <AnimatePresence mode="wait">
            {isResultStep ? (
              <motion.div
                key="result"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                data-testid="quiz-result"
                className="flex flex-col flex-1"
              >
                <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-teal-700 mb-3">
                  <Sparkles className="h-4 w-4" /> {result.score_label}
                </div>
                <h2 className="font-display text-3xl md:text-4xl font-extrabold text-slate-900 leading-tight mb-5">
                  Your portfolio could unlock significant value.
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-6">
                  <ResultCard
                    label="Untapped Revenue Opportunity"
                    low={result.opportunity_low}
                    high={result.opportunity_high}
                    color="indigo"
                  />
                  <ResultCard
                    label="Annual OPEX Savings"
                    low={result.savings_low}
                    high={result.savings_high}
                    color="teal"
                  />
                </div>
                <p className="text-slate-700 text-[15px] leading-relaxed mb-8">{result.message}</p>
                <div className="flex flex-wrap items-center gap-3">
                  <Link
                    to="/#contact"
                    data-testid="quiz-cta-analysis"
                    className="inline-flex items-center gap-2 btn-primary px-6 py-3.5 text-sm font-semibold"
                  >
                    Get Detailed Portfolio Analysis <ArrowRight className="h-4 w-4" />
                  </Link>
                  <button
                    onClick={reset}
                    data-testid="quiz-reset"
                    className="inline-flex items-center gap-2 btn-ghost px-6 py-3.5 text-sm font-semibold"
                  >
                    <RotateCcw className="h-4 w-4" /> Retake Quiz
                  </button>
                </div>
              </motion.div>
            ) : isContactStep ? (
              <motion.div
                key="contact"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                className="flex flex-col flex-1"
                data-testid="quiz-contact-step"
              >
                <h2 className="font-display text-2xl md:text-3xl font-bold text-slate-900 mb-3">
                  Where should we send your tailored insights?
                </h2>
                <p className="text-slate-600 text-[15px] mb-6">
                  Optional — skip and we'll just show your result on screen.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <FieldLabel label="Name">
                    <Input
                      data-testid="quiz-input-name"
                      value={contact.name}
                      onChange={(e) => setContact({ ...contact, name: e.target.value })}
                      placeholder="Aarav"
                      className="h-11 rounded-none border-slate-300 focus-visible:ring-[#5B6BD6]"
                    />
                  </FieldLabel>
                  <FieldLabel label="Email">
                    <Input
                      data-testid="quiz-input-email"
                      type="email"
                      value={contact.email}
                      onChange={(e) => setContact({ ...contact, email: e.target.value })}
                      placeholder="aarav@pharma.co"
                      className="h-11 rounded-none border-slate-300 focus-visible:ring-[#5B6BD6]"
                    />
                  </FieldLabel>
                  <FieldLabel label="Company">
                    <Input
                      data-testid="quiz-input-company"
                      value={contact.company}
                      onChange={(e) => setContact({ ...contact, company: e.target.value })}
                      placeholder="Pharma Co."
                      className="h-11 rounded-none border-slate-300 focus-visible:ring-[#5B6BD6]"
                    />
                  </FieldLabel>
                </div>
                <div className="flex items-center gap-3 mt-auto">
                  <button
                    onClick={() => setStep(step - 1)}
                    className="inline-flex items-center gap-1.5 btn-ghost px-5 py-3 text-sm font-semibold"
                  >
                    <ArrowLeft className="h-4 w-4" /> Back
                  </button>
                  <button
                    onClick={submit}
                    disabled={loading}
                    data-testid="quiz-submit"
                    className="inline-flex items-center gap-2 btn-primary px-6 py-3 text-sm font-semibold disabled:opacity-70"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" /> Calculating…
                      </>
                    ) : (
                      <>Reveal My Result <ArrowRight className="h-4 w-4" /></>
                    )}
                  </button>
                </div>
              </motion.div>
            ) : (
              <motion.div
                key={`q-${step}`}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                className="flex flex-col flex-1"
                data-testid={`quiz-question-${step}`}
              >
                <div className="text-[11px] font-bold uppercase tracking-[0.16em] text-slate-500 mb-2">
                  Question {step + 1}
                </div>
                <h2 className="font-display text-2xl md:text-3xl font-bold text-slate-900 leading-tight mb-7">
                  {current.question}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {current.options.map((opt) => {
                    const selected = answers[current.id] === opt;
                    const display = `${current.prefix || ""}${opt}${current.suffix || ""}`;
                    return (
                      <button
                        key={opt}
                        onClick={() => choose(opt)}
                        data-testid={`quiz-option-${step}-${opt}`}
                        className={`text-left px-5 py-4 border transition-all ${
                          selected
                            ? "border-slate-900 bg-slate-900 text-white"
                            : "border-slate-300 bg-white text-slate-800 hover:border-slate-900"
                        }`}
                      >
                        <span className="font-semibold text-[15px]">{display}</span>
                      </button>
                    );
                  })}
                </div>

                {step > 0 && (
                  <div className="mt-auto pt-8">
                    <button
                      onClick={() => setStep(step - 1)}
                      data-testid="quiz-back"
                      className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900"
                    >
                      <ArrowLeft className="h-4 w-4" /> Previous
                    </button>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <p className="text-xs text-slate-500 mt-4">
          Estimates are directional — based on industry benchmarks and your inputs. A detailed analysis
          requires portfolio review by Medstry's experts.
        </p>
      </div>
    </main>
  );
}

function ResultCard({ label, low, high, color }) {
  const accent = color === "teal" ? "text-teal-700" : "text-indigo-600";
  return (
    <div className="bg-slate-50 border border-slate-200 p-6">
      <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500 mb-3">
        {label}
      </div>
      <div className={`font-display text-3xl md:text-4xl font-extrabold tnum ${accent}`}>
        ₹{low.toFixed(0)}–{high.toFixed(0)} Cr
      </div>
      <div className="text-xs text-slate-500 mt-2">Per annum, estimated</div>
    </div>
  );
}

function FieldLabel({ label, children }) {
  return (
    <div>
      <Label className="text-[11px] uppercase tracking-[0.16em] font-bold text-slate-600 mb-2 block">
        {label}
      </Label>
      {children}
    </div>
  );
}
